import { isServer, html } from 'lit';
import { query, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { CrntElement } from '../../lib/crnt-element.js';
import { trustedCSS } from '../../lib/css/css.js';
import { CrntBlurEvent } from '../../lib/events/blur.js';
import { CrntFocusEvent } from '../../lib/events/focus.js';
import { AnchorElementMixin } from '../anchor/anchor-element-mixin.js';
import { CrntButtonBaseMixin } from '../button-base/button-base-mixin.js';
import { ButtonElementMixin } from './button-element-mixin.js';
import buttonStyles from './button.css.js';

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
class CrntButton extends ButtonElementMixin(AnchorElementMixin(CrntButtonBaseMixin(CrntElement))) {
  constructor() {
    super(...arguments);
    /** @internal Internals for form-associated custom elements. */
    this.internals = null;
    this.ariaLabel = null;
  }
  static {
    /** ==================================================================================================================
     * @region Internal logic
     ================================================================================================================== */
    /** @internal */
    this.formAssociated = true;
  }
  static {
    /** @internal */
    this.shadowRootOptions = {
      ...CrntElement.shadowRootOptions,
      delegatesFocus: true
    };
  }
  static {
    /** @internal */
    this.styles = [CrntButtonBaseMixin(CrntElement).styles || [], trustedCSS(buttonStyles)];
  }
  static {
    /** @internal */
    this.tagName = "crnt-button";
  }
  /** ==================================================================================================================
   * @region Programmatic events
   ================================================================================================================== */
  /** Removes focus from the container element. */
  blur() {
    if (this.container) {
      this.container.blur();
    } else {
      super.blur();
    }
  }
  /** Simulates a click on the button. */
  click() {
    if (this.container) {
      this.container.click();
    } else {
      super.click();
    }
  }
  /** Sets focus on the container element. */
  focus(options) {
    if (this.container) {
      this.container.focus(options);
    } else {
      super.focus(options);
    }
  }
  /** ==================================================================================================================
   * @region Events handlers
   ================================================================================================================== */
  /** @internal */
  #handleBlur() {
    this.dispatchEvent(new CrntBlurEvent());
  }
  /** @internal */
  #handleButtonClick(event) {
    if (this._isDisabled) {
      event.stopImmediatePropagation();
      return;
    }
    super.handleButtonClick();
  }
  /** @internal */
  #handleFocus() {
    this.dispatchEvent(new CrntFocusEvent());
  }
  /** ==================================================================================================================
   * @region Lifecycle events
   ================================================================================================================== */
  connectedCallback() {
    super.connectedCallback();
    if (!isServer) {
      this.internals = this.attachInternals();
      if (!this.isAnchor) {
        this.internals.role = "button";
      }
      this.addEventListener?.("click", this.#handleButtonClick.bind(this));
    }
  }
  /** ==================================================================================================================
   * @region Render methods
   ================================================================================================================== */
  /** @internal Render the component. */
  render() {
    const content = this.#renderContent();
    return this.isAnchor ? this.#renderAnchor(content) : this.#renderButton(content);
  }
  /** @internal Render the component as an anchor tag. */
  #renderAnchor(content) {
    return html`<a
			part="container"
			class=${this._containerClasses}
			href=${ifDefined(this.href)}
			target=${this.target}
			aria-label=${ifDefined(this.ariaLabel ?? void 0)}
			aria-disabled=${ifDefined(this._isDisabled === true ? "true" : void 0)}
			@blur=${this.#handleBlur}
			@focus=${this.#handleFocus}
			>${content}</a
		>`;
  }
  /** @internal Render the component as an button tag. */
  #renderButton(content) {
    return html`<button
			part="container"
			class=${this._containerClasses}
			aria-disabled=${ifDefined(this._isDisabled === true ? "true" : void 0)}
			type=${this.type}
			aria-label=${ifDefined(this.ariaLabel ?? void 0)}
			@blur=${this.#handleBlur}
			@focus=${this.#handleFocus}
		>
			${content}
		</button>`;
  }
  /** @internal Render the inner content of the button. */
  #renderContent() {
    return html`
			<span class="crnt-button__leading" part="leading">
				<slot name="leading"></slot>
			</span>
			<span class="crnt-button__content" part="content">
				<slot></slot>
			</span>
			<span class="crnt-button__trailing" part="trailing">
				<slot name="trailing"></slot>
				<slot name="fixed-trailing"></slot>
			</span>
		`;
  }
}
__decorateClass([
  query('[part="container"]')
], CrntButton.prototype, "container");
__decorateClass([
  property({ attribute: "aria-label" })
], CrntButton.prototype, "ariaLabel");

export { CrntButton };
